'use client';

import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import type { JSX } from 'react';
import type { ParagraphType } from '../../../../interfaces';
import { classes } from '../../../utils/classes';
import type { GroupType } from '../../CmsGroup/types';
import { ComponentRecognizer } from '../../ComponentRecognizer/ComponentRecognizer';

export const ImageGroup = ({ data }: { data: GroupType }): JSX.Element => {
  const isDesktop = useMediaType();
  const {
    field_cells_per_row: cellsPerRowDesktop = data.paragraph ? data?.paragraph?.length || 1 : 0,
    field_cells_per_row_mobile: cellsPerRowMobile = (cellsPerRowDesktop &&
      Math.floor(cellsPerRowDesktop / 2)) ||
      1,
    field_col_gap: colGap = '0',
    field_row_gap: rowGap = '0',
    field_padding: padding,
    field_max_width: maxWidth = 'none',
    field_footer_layout_variant: layoutVariant = 'default',
  } = data;

  const cellsPerRowPercentageMobile = 100 / cellsPerRowMobile;
  const cellsPerRowPercentage = 100 / cellsPerRowDesktop;

  const flexBasisMob =
    colGap === '0'
      ? `calc(100%/${cellsPerRowMobile})`
      : `calc((100% - ${colGap} * ${cellsPerRowMobile - 1}) / ${cellsPerRowMobile})`;

  const flexBasisDesktop =
    colGap === '0'
      ? `calc(100%/${cellsPerRowDesktop})`
      : `calc((100% - ${colGap} * ${cellsPerRowDesktop - 1}) / ${cellsPerRowDesktop})`;

  return (
    <div
      className={`m-0 box-border flex-row flex-wrap justify-start ${layoutVariant === 'inline' ? 'inline-flex' : 'flex'}`}
      style={{ padding }}
    >
      {data?.paragraph?.map((item: ParagraphType, index: number) => (
        <ComponentRecognizer
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={`image-group-item-${index}`}
          className={classes(
            // if the items should have 100% with - do the alignment as column
            cellsPerRowPercentageMobile === 100 ? 'flex-col' : 'flex-row',
            // only apply the specific desktop stlye if it differs from the mobile style
            cellsPerRowMobile !== cellsPerRowDesktop && cellsPerRowPercentage === 100
              ? 'md:flex-col'
              : 'md:flex-row',
            cellsPerRowMobile === 1 ? 'mr-auto mb-auto' : null,
            // only apply the specific desktop stlye if it differs from the mobile style
            cellsPerRowMobile !== cellsPerRowDesktop && cellsPerRowDesktop === 1 && 'md:mr-auto',
            // getting rid of the default margin right for the last item in the row
            (index + 1) % cellsPerRowMobile === 0 ? 'mr-0!' : null,
            // only apply the specific desktop stlye if it differs from the mobile style
            cellsPerRowMobile !== cellsPerRowDesktop &&
              (index + 1) % cellsPerRowDesktop === 0 &&
              'md:mr-0!',
          )}
          style={{
            maxWidth,
            ...(cellsPerRowMobile > 1 && {
              flexBasis: flexBasisMob,
              marginRight: colGap,
              marginBottom: rowGap,
            }),
            ...(isDesktop && {
              marginBottom: rowGap,
              ...(cellsPerRowDesktop > 1 && { flexBasis: flexBasisDesktop, marginRight: colGap }),
            }),
          }}
          item={item}
        />
      ))}
    </div>
  );
};
